import {
  Badge,
  Chip,
  Tooltip,
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import RadarOutlinedIcon from '@mui/icons-material/RadarOutlined';
import { NavLink, useLocation } from 'react-router-dom';
import { SECTION_ORDER } from '../nav';
import { visibleNavItems } from '../navVisibility';
import { useNavAuth } from '../hooks/useNavAuth';
import { useTenantBranding } from '../theme/TenantThemeProvider';
import { NAV_FOREGROUND_LIGHT, readableOn } from '@oip/contracts';
import { useTheme } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { BuildVersion } from './BuildVersion';
import { visuallyHidden } from '../utils/visuallyHidden';
import { badgeDescription, badgeLabel } from '../navCounts';
import { useNavCounts } from '../hooks/api';

export const DRAWER_WIDTH = 248;

/**
 * The collapsed rail.
 *
 * ⚠️ **72px is the ICON's requirement, not a round number.** A 24px glyph centred with enough
 * room either side to be a comfortable target, plus the 8px gutter the expanded list already
 * uses. Narrower and the touch target drops under the 44px minimum; wider and it stops reading
 * as a rail and starts reading as a menu that has lost its labels.
 */
export const RAIL_WIDTH = 72;

function isActive(pathname: string, path: string): boolean {
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function SideNav({
  onNavigate,
  collapsed = false,
  onToggleCollapse,
}: {
  onNavigate?: () => void;
  /**
   * Icon-only. ⚠️ **NEVER on the temporary drawer**, which a reader opens BY TAPPING a menu
   * button — collapsing it there would give them a rail they asked to see the labels of.
   */
  collapsed?: boolean;
  /** Absent on the phone drawer, where the control has nothing to do. */
  onToggleCollapse?: () => void;
}) {
  const navAuth = useNavAuth();
  const counts = useNavCounts();
  const { pathname } = useLocation();
  const branding = useTenantBranding();
  const theme = useTheme();
  /*
   * With ACCENT the sidebar is filled with the tenant's primary and the
   * foreground is DERIVED — whichever of white or near-black clears AA against
   * it. That derivation is the whole reason this is a named choice rather than a
   * colour field: a tenant picking a background would also have to pick readable
   * text, and most would not.
   */
  const accentNav = branding?.navStyle === 'ACCENT' && Boolean(branding.accent);
  const navBg = accentNav ? theme.palette.primary.main : 'background.paper';
  const navFg = accentNav ? readableOn(theme.palette.primary.main) : undefined;

  /*
   * The selected and hover washes follow the FOREGROUND, not white.
   *
   * A white overlay is right on a dark accent and nearly invisible on a light one,
   * where the readable foreground is near-black. Deriving it from `navFg` keeps the
   * two in step for any accent a tenant can choose.
   */
  const overlay = (alpha: number): string =>
    navFg === NAV_FOREGROUND_LIGHT ? `rgba(255,255,255,${alpha})` : `rgba(0,0,0,${alpha})`;

  /*
   * The selected item, which is where this went wrong.
   *
   * On a paper nav the selected row is tinted grey with accent-coloured text. Applied
   * unchanged to an accent-filled nav that paints the accent ON the accent —
   * `primary.main` text on a `primary.main` background, a contrast ratio of 1:1 —
   * so the current page became the one item nobody could read. The same mistake the
   * sidebar mark made, in a different place.
   */
  const selectedSx = accentNav
    ? {
        bgcolor: overlay(0.22),
        '& .MuiListItemIcon-root, & .MuiListItemText-primary': { color: navFg },
        fontWeight: 700,
        '&:hover': { bgcolor: overlay(0.3) },
      }
    : {
        bgcolor: 'action.selected',
        '& .MuiListItemIcon-root, & .MuiListItemText-primary': { color: 'primary.main' },
        fontWeight: 700,
      };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: navBg,
        ...(navFg
          ? {
              color: navFg,
              // Descendants that set their own colour must follow too, or the
              // section headings and icons stay grey on a saturated background.
              '& .MuiListItemIcon-root': { color: navFg },
              '& .MuiTypography-root': { color: navFg },
              // The selected row is styled on the item itself (`selectedSx`), so it
              // is decided in ONE place rather than by whichever rule wins.
              '& .MuiListItemButton-root:hover': { bgcolor: overlay(0.1) },
            }
          : {}),
      }}
    >
      {/*
        The wordmark, branded when the tenant has configured one.
        A logo REPLACES the mark and the name rather than sitting beside them — a
        customer's logo next to ours reads as a partnership, which it is not.
      */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: collapsed ? 0 : 2,
          justifyContent: collapsed ? 'center' : undefined,
          height: 56,
          flexShrink: 0,
        }}
      >
        {branding?.logoUrl ? (
          <Box
            component="img"
            src={branding.logoUrl}
            alt={branding.displayName ?? 'Logo'}
            sx={{ maxHeight: 32, maxWidth: collapsed ? 40 : 168, objectFit: 'contain' }}
          />
        ) : (
          <>
            {/*
              On an accent-filled nav the mark must use the derived foreground:
              `color="primary"` would paint it in the accent, on the accent, and
              it disappears. Caught by looking at it, not by a test.
            */}
            <RadarOutlinedIcon
              {...(accentNav ? { sx: { color: navFg } } : { color: 'primary' as const })}
            />
            {/*
              ⚠️ Collapsed, the MARK stays and the NAME goes. A wordmark truncated to "Hale…"
              is worse than the mark alone — a customer's brand rendered wrong is the one thing
              a white-label console must not do, and the mark is what a reader recognises at a
              glance anyway.
            */}
            <Box sx={{ lineHeight: 1, display: collapsed ? 'none' : undefined }}>
              <Typography sx={{ fontWeight: 720, fontSize: '0.95rem', letterSpacing: '-0.01em' }}>
                {branding?.displayName ?? 'OIP'}
              </Typography>
              {/* The strapline is ours, so it is dropped once a tenant has named
                  the product themselves — keeping it would caption their brand
                  with our words. */}
              {!branding?.displayName && (
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.04em' }}>
                  Command Centre
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>

      {/*
        ⚠️ **THE DESCRIPTIONS LIVE OUTSIDE EVERY LINK, and that placement IS the mechanism.**
        `aria-describedby` may point anywhere in the document; text INSIDE the anchor would join
        its accessible name instead, which is precisely what this arrangement exists to prevent —
        `getByRole('link', { name: 'Incidents' })` in two e2e specs would then match or not
        according to how much data the database held. Rendered once rather than per row because
        each count appears on at most one item, and a duplicated `id` is invalid.

        `visuallyHidden` rather than `display: none`: the latter removes it from the
        accessibility tree, so the description would exist and be announced by nobody.
      */}
      {(['incidents', 'recommendations'] as const).map((key) => {
        const means = badgeDescription(key, counts[key]);
        return means ? (
          <Box key={key} component="span" id={`nav-count-${key}`} sx={visuallyHidden}>
            {means}
          </Box>
        ) : null;
      })}

      <Box sx={{ overflowY: 'auto', flex: 1, pb: 2 }}>
        {SECTION_ORDER.map((section) => {
          const items = visibleNavItems(section, navAuth);
          if (items.length === 0) return null;
          // Platform administration is a different scope, not another group of
          // tenant features — the rule separates it visually.
          const separated = section === 'Platform';
          return (
            <Box key={section} sx={{ mt: 1.5 }}>
              {separated && <Divider sx={{ mx: collapsed ? 1.5 : 2, mb: 1.5 }} />}
              {/*
                ⚠️ **THE HEADING BECOMES A RULE, never a truncated word.** "INSIGHTS" does not
                fit in 72px and an ellipsis reads as a rendering fault; dropping it entirely
                would run two groups together, which is the one thing the sections are for. A
                rule keeps the grouping and claims to say nothing — and the grouping still
                reaches a screen reader, which reads the visually-hidden name below.
              */}
              {collapsed ? (
                !separated && <Divider sx={{ mx: 1.5, mb: 1 }} />
              ) : (
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ px: 2.5, display: 'block', mb: 0.5 }}
                >
                  {section}
                </Typography>
              )}
              <List dense disablePadding>
                {items.map((item) => {
                  const active = isActive(pathname, item.path);
                  const countText = item.attentionCount
                    ? badgeLabel(counts[item.attentionCount])
                    : null;
                  const countMeans = item.attentionCount
                    ? badgeDescription(item.attentionCount, counts[item.attentionCount])
                    : null;
                  const countId = `nav-count-${item.attentionCount}`;
                  /*
                   * ⚠️ **THE TOOLTIP IS THE LABEL, and collapsed it is the ONLY way to read
                   * one.** Icons are recognisable once learnt and this product has thirteen of
                   * them; a rail without tooltips is a memory test. It carries the COUNT too,
                   * because the pill beside the label — which said what the number meant — is
                   * gone at this width, so without it the dot on the icon is a number with no
                   * subject.
                   */
                  const tip = collapsed
                    ? [item.label, countMeans].filter(Boolean).join(' · ')
                    : '';
                  const row = (
                    <ListItemButton
                      key={item.path}
                      component={NavLink}
                      to={item.path}
                      onClick={onNavigate}
                      selected={active}
                      /*
                       * ⚠️ A DESCRIPTION, never the name. `aria-describedby` is announced AFTER
                       * the accessible name and does not change it — so a screen reader hears
                       * "Incidents, link, 28 open now" while `getByRole('link', { name:
                       * 'Incidents' })` in two e2e specs still matches exactly. Folding the
                       * number into the name would make those specs pass or fail according to
                       * how much data the database held, which is a flake rather than a break.
                       */
                      aria-describedby={countMeans ? countId : undefined}
                      /*
                       * ⚠️ **COLLAPSED, THE LABEL IS THE `aria-label`, and without it the link
                       * has NO ACCESSIBLE NAME AT ALL.** Removing `ListItemText` removes the only
                       * text inside the anchor, so a screen reader announces "link" and
                       * `getByRole('link', { name: 'Incidents' })` — which two e2e specs use —
                       * matches nothing. The name is identical in both states by construction,
                       * so a spec cannot depend on which one a reader is in.
                       */
                      aria-label={collapsed ? item.label : undefined}
                      sx={{
                        py: 0.6,
                        ...(collapsed
                          ? { justifyContent: 'center', px: 0, mx: 1, borderRadius: 1 }
                          : {}),
                        '&.Mui-selected': selectedSx,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed ? 0 : 34,
                          color: 'text.secondary',
                          justifyContent: 'center',
                        }}
                      >
                        {/*
                          ⚠️ Collapsed, the count rides the ICON as a dot rather than sitting
                          beside it: 72px has no room for a pill, and a number that overflowed
                          the rail is the horizontal-overflow fault the layout rules forbid. It
                          is still `aria-hidden` and still described by the same sentence, so
                          what a screen reader hears does not change with the width.
                        */}
                        {collapsed && countText ? (
                          <Badge
                            badgeContent={countText}
                            aria-hidden
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '0.6rem',
                                height: 15,
                                minWidth: 15,
                                fontWeight: 700,
                                fontVariantNumeric: 'tabular-nums',
                                bgcolor: 'action.selected',
                                color: 'text.secondary',
                                border: 1,
                                borderColor: 'background.paper',
                              },
                            }}
                          >
                            {item.icon}
                          </Badge>
                        ) : (
                          item.icon
                        )}
                      </ListItemIcon>
                      {!collapsed && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.83rem',
                            fontWeight: active ? 700 : 500,
                          }}
                        />
                      )}
                      {/*
                        The attention count. ⚠️ **NEUTRAL, never red**, for the reason the `Beta`
                        chip below is: green, amber and red are the four risk levels on every
                        badge and every score in this product, so a red `28` beside Incidents
                        would claim CRITICAL severity for an ordinary open count. The one figure
                        that IS a severity — `criticalOpenNow` — is deliberately not shown here:
                        one badge per row, and the dashboard tile already carries it in red.
                      */}
                      {!collapsed && countText && (
                        <>
                          <Box
                            aria-hidden
                            component="span"
                            title={countMeans ?? undefined}
                            sx={{
                              ml: 0.75,
                              px: 0.75,
                              minWidth: 20,
                              height: 18,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 9,
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              fontVariantNumeric: 'tabular-nums',
                              bgcolor: 'action.selected',
                              color: 'text.secondary',
                            }}
                          >
                            {countText}
                          </Box>
                        </>
                      )}
                      {/*
                        ⚠️ `aria-hidden`, so the link's accessible name stays the
                        LABEL. Without it a screen reader announces "Anomalies
                        Beta" and anything finding the item by name has to know
                        about the badge — the fault the copilot's question cards
                        already record.
                      */}
                      {!collapsed && item.badge && (
                        <Chip
                          aria-hidden
                          label={item.badge}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.62rem',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            // Neutral on purpose: green, amber and red are the
                            // four risk levels, and a coloured badge here would
                            // claim a severity by being a colour.
                            bgcolor: 'action.selected',
                            color: 'text.secondary',
                            '& .MuiChip-label': { px: 0.7 },
                          }}
                        />
                      )}
                    </ListItemButton>
                  );
                  return collapsed ? (
                    <Tooltip key={item.path} title={tip} placement="right">
                      {/* A span, because `ListItemButton` forwards its ref but a disabled or
                          re-rendered NavLink inside a Tooltip is fragile without one. */}
                      <span>{row}</span>
                    </Tooltip>
                  ) : (
                    row
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {/*
       * ⚠️ **SETTINGS IS A FOOTER ITEM, not a section, and that is what the redesign asked for.**
       * Seventeen pages left the daily menu; without one entry that reaches them they would be
       * findable only through the command palette or a bookmark. Pinned outside the scrolling
       * list beside the version, so it does not move as the menu grows.
       *
       * Offered to EVERY role deliberately. The page renders only what `isNavItemVisible` allows
       * and says so when that is nothing — where hiding the entry for a VIEWER would leave them
       * unable to reach `My Notifications`, which is theirs.
       */}
      <List dense disablePadding sx={{ px: 1, pb: 0.5 }}>
        <Tooltip title={collapsed ? 'Settings' : ''} placement="right">
          <ListItemButton
            component={NavLink}
            to="/settings"
            onClick={onNavigate}
            selected={isActive(pathname, '/settings')}
            aria-label={collapsed ? 'Settings' : undefined}
            sx={{
              py: 0.6,
              borderRadius: 1,
              ...(collapsed ? { justifyContent: 'center', px: 0 } : {}),
              '&.Mui-selected': selectedSx,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: collapsed ? 0 : 34,
                color: 'text.secondary',
                justifyContent: 'center',
              }}
            >
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: '0.875rem' }} />
            )}
          </ListItemButton>
        </Tooltip>
      </List>

      {/*
        ⚠️ **THE TOGGLE IS IN THE NAV, not the header, and it is the only control that can
        bring the labels back.** In the header it would sit among controls that act on the
        PAGE — search, notifications, the account — where this acts on the menu beside it; and
        a reader who has collapsed the rail looks for the way back where they left it.

        Absent on the phone drawer (`onToggleCollapse` undefined there), where the drawer is
        opened by tapping a button precisely to see the labels: collapsing it would be a
        control that undoes what the reader just asked for.
      */}
      {onToggleCollapse && (
        <List dense disablePadding sx={{ px: 1, pb: 0.5 }}>
          <Tooltip title={collapsed ? 'Expand menu' : 'Collapse menu'} placement="right">
            <ListItemButton
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
              /*
               * ⚠️ It reports its own STATE, not just its action. `aria-expanded` is what tells
               * a screen-reader user which way the rail currently is — without it the button
               * announces "Expand menu" and nothing says whether that has happened.
               */
              aria-expanded={!collapsed}
              sx={{
                py: 0.6,
                borderRadius: 1,
                ...(collapsed ? { justifyContent: 'center', px: 0 } : {}),
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 34,
                  color: 'text.secondary',
                  justifyContent: 'center',
                }}
              >
                {collapsed ? (
                  <ChevronRightIcon fontSize="small" />
                ) : (
                  <ChevronLeftIcon fontSize="small" />
                )}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary="Collapse menu"
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </List>
      )}

      {/*
        Outside the scrolling list, so it stays visible however long the nav
        grows. Somebody asking "what version is this" is usually looking at a
        screen that is behaving oddly, and making them scroll to find out is the
        moment they give up and ask instead.
      */}
      <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
        <BuildVersion compact={collapsed} />
      </Box>
    </Box>
  );
}
